<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import Signal from '@/api/Signal'
import { formatRelativeDate } from '@/lib/formatDate'

const toast = useToast()

const feeds = ref([])
const loading = ref(true)
const adding = ref(false)
const feedUrl = ref('')
const deletingId = ref(null)
const confirmingDeleteId = ref(null)

async function loadFeeds() {
  loading.value = true
  try {
    const res = await Signal.listFeeds()
    if (res.status === 'success') {
      feeds.value = res.data || []
    } else {
      toast.error(res.message || 'Failed to load feeds')
    }
  } finally {
    loading.value = false
  }
}

function isValidUrl(str) {
  try {
    const u = new URL(str)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

async function addFeed() {
  const trimmed = feedUrl.value.trim()
  if (!trimmed) return

  if (!isValidUrl(trimmed)) {
    toast.error('Please enter a valid URL (http:// or https://)')
    return
  }

  adding.value = true
  try {
    const res = await Signal.createFeed(trimmed)
    if (res.status === 'success') {
      feeds.value.unshift(res.data)
      feedUrl.value = ''
      toast.success('Feed added successfully')
    } else if (res.status === 'validation_error' || res.status === 'conflict') {
      toast.error(res.message)
    } else {
      toast.error(res.message || 'Failed to add feed')
    }
  } finally {
    adding.value = false
  }
}

async function toggleFeed(feed) {
  const original = feed.enabled
  feed.enabled = !original
  const res = await Signal.updateFeed(feed.id, { enabled: feed.enabled })
  if (res.status !== 'success') {
    feed.enabled = original
    toast.error(res.message || 'Failed to update feed')
  }
}

function confirmDelete(feed) {
  if (confirmingDeleteId.value === feed.id) {
    removeFeed(feed)
  } else {
    confirmingDeleteId.value = feed.id
    setTimeout(() => {
      if (confirmingDeleteId.value === feed.id) confirmingDeleteId.value = null
    }, 3000)
  }
}

async function removeFeed(feed) {
  confirmingDeleteId.value = null
  deletingId.value = feed.id
  try {
    const res = await Signal.deleteFeed(feed.id)
    if (res.status === 'success') {
      feeds.value = feeds.value.filter(f => f.id !== feed.id)
      toast.success('Feed removed')
    } else {
      toast.error(res.message || 'Failed to delete feed')
    }
  } finally {
    deletingId.value = null
  }
}

onMounted(loadFeeds)
</script>

<template>
  <div class="feed-management">
    <!-- Add feed form -->
    <form class="add-form" @submit.prevent="addFeed">
      <div class="url-input-wrap">
        <font-awesome-icon :icon="['fas', 'rss']" class="input-icon" />
        <input
          v-model="feedUrl"
          type="text"
          placeholder="https://example.com/feed.xml"
          :disabled="adding"
          class="url-input"
        />
      </div>
      <button
        type="submit"
        class="btn-add"
        :disabled="adding || !feedUrl.trim()"
      >
        <font-awesome-icon v-if="adding" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'plus']" />
        Add Feed
      </button>
    </form>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
      <p>Loading feeds...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="feeds.length === 0" class="empty-state">
      <font-awesome-icon :icon="['fas', 'rss']" class="empty-icon" />
      <h3>No feeds configured</h3>
      <p>Add an RSS or Atom feed URL above to start ingesting threat intelligence signals automatically.</p>
    </div>

    <!-- Feed list -->
    <div v-else class="feed-list">
      <div
        v-for="feed in feeds"
        :key="feed.id"
        class="feed-row"
        :class="{ disabled: !feed.enabled }"
      >
        <div class="feed-info">
          <div class="feed-title-row">
            <span class="feed-title">{{ feed.title || feed.url }}</span>
            <font-awesome-icon
              v-if="feed.last_error"
              :icon="['fas', 'exclamation-triangle']"
              class="error-indicator"
              :title="feed.last_error"
            />
          </div>

          <span v-if="feed.title" class="feed-url">{{ feed.url }}</span>

          <div class="feed-meta">
            <span v-if="feed.suggested_by" class="meta-label">
              <font-awesome-icon :icon="['fas', 'user']" class="meta-icon" />
              Suggested by {{ feed.suggested_by_email || feed.suggested_by }}
            </span>
            <span v-if="feed.added_by" class="meta-label">
              <font-awesome-icon :icon="['fas', 'user']" class="meta-icon" />
              Added by {{ feed.added_by_email || feed.added_by }}
            </span>
            <span class="meta-label">
              <font-awesome-icon :icon="['fas', 'clock']" class="meta-icon" />
              {{ feed.last_fetched_at ? formatRelativeDate(feed.last_fetched_at) : 'Never fetched' }}
            </span>
          </div>
        </div>

        <div class="feed-actions">
          <!-- Toggle switch -->
          <label class="toggle" :title="feed.enabled ? 'Disable feed' : 'Enable feed'">
            <input
              type="checkbox"
              :checked="feed.enabled"
              @change="toggleFeed(feed)"
            />
            <span class="toggle-slider"></span>
          </label>

          <!-- Delete button (click once to confirm, click again to delete) -->
          <button
            class="btn-delete"
            :class="{ confirming: confirmingDeleteId === feed.id }"
            @click="confirmDelete(feed)"
            :disabled="deletingId === feed.id"
            :title="confirmingDeleteId === feed.id ? 'Click again to confirm' : 'Delete feed'"
          >
            <font-awesome-icon
              v-if="deletingId === feed.id"
              :icon="['fas', 'spinner']"
              spin
            />
            <template v-else-if="confirmingDeleteId === feed.id">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
            </template>
            <font-awesome-icon v-else :icon="['fas', 'trash']" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.feed-management {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: var(--text-heading);
  overflow-y: auto;
  padding: 1.5rem 2rem;
}

/* Add feed form */
.add-form {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.url-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  background: var(--surface-overlay);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.url-input-wrap:focus-within {
  border-color: var(--accent);
}

.input-icon {
  padding-left: 0.75rem;
  color: var(--text-disabled);
  font-size: 0.8125rem;
  flex-shrink: 0;
}

.url-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 0.625rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: inherit;
}

.url-input::placeholder {
  color: var(--text-disabled);
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-sm);
  color: var(--accent-text);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-add:hover:not(:disabled) {
  background: var(--accent-soft-4);
  border-color: rgba(115, 87, 210, 0.5);
}

.btn-add:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  color: var(--text-muted);
}

.loading-icon {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
}

.loading-state p {
  font-size: 0.8125rem;
  margin: 0;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1rem;
  color: var(--text-faint);
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  opacity: 0.3;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.125rem;
  color: var(--text-muted);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.8125rem;
  margin: 0;
  max-width: 360px;
  line-height: 1.5;
}

/* Feed list */
.feed-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.feed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
}

.feed-row:hover {
  background: var(--surface-overlay);
  border-color: var(--border-subtle);
}

.feed-row.disabled {
  opacity: 0.45;
}

.feed-row.disabled:hover {
  opacity: 0.55;
}

/* Feed info */
.feed-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.feed-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.feed-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-indicator {
  color: var(--color-error-text);
  font-size: 0.75rem;
  flex-shrink: 0;
  cursor: help;
}

.feed-url {
  font-size: 0.75rem;
  color: var(--text-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Monaco', 'Courier New', monospace;
}

.feed-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.125rem;
}

.meta-label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.6875rem;
  color: var(--text-faint);
  white-space: nowrap;
}

.meta-icon {
  font-size: 0.5625rem;
  color: var(--text-disabled);
}

/* Feed actions */
.feed-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

/* Toggle switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  cursor: pointer;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--surface-overlay-3);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  transition: var(--transition);
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  left: 2px;
  bottom: 2px;
  background: var(--text-faint);
  border-radius: var(--radius-pill);
  transition: var(--transition);
}

.toggle input:checked + .toggle-slider {
  background: var(--accent-soft-4);
  border-color: rgba(115, 87, 210, 0.4);
}

.toggle input:checked + .toggle-slider::before {
  transform: translateX(16px);
  background: var(--accent-text);
}

.toggle:hover .toggle-slider {
  border-color: rgba(255, 255, 255, 0.2);
}

.toggle input:checked + .toggle-slider:hover {
  border-color: rgba(115, 87, 210, 0.5);
}

/* Delete button */
.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-xs);
  color: var(--text-disabled);
  cursor: pointer;
  font-size: 0.6875rem;
  transition: var(--transition-fast);
}

.btn-delete:hover:not(:disabled) {
  color: var(--color-error);
  background: var(--color-error-soft);
  border-color: rgba(239, 68, 68, 0.2);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-delete.confirming {
  color: var(--color-warning-text);
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

/* Scrollbar */
.feed-management::-webkit-scrollbar {
  width: 6px;
}

.feed-management::-webkit-scrollbar-track {
  background: transparent;
}

.feed-management::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: var(--radius-xs);
}
</style>
